/**
 * `/reports` — §19 reporting cadence: next-business-day report, §23.2 hypercare dashboard, and the campaign
 * close report per lottery (`/reports/lotteries/:id`): cohort funnel, incremental referrals, net financials from
 * the ledger, audience growth, sponsor delivery.
 */
import { useQuery } from '@tanstack/react-query';
import type { CampaignReportDto, MetricValueDto } from '@windrop/contracts';
import { Button, Skeleton, useIntl } from '@windrop/ui';
import { useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router';
import { BarsChart } from '../../components/Chart';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { lotteriesApi } from '../../lib/api/admin';
import { analyticsApi, exportsApi } from '../../lib/api/analytics';
import { isApiError } from '../../lib/api/client';
import { formatMetric } from '../dashboard/Dashboard';

export const REPORT_TABS = [
  { id: '', label: 'Next business day' },
  { id: 'hypercare', label: 'Hypercare' },
  { id: 'campaigns', label: 'Campaign close' },
] as const;

export function ReportsPage() {
  return (
    <>
      <PageHeader
        title="Reports"
        description="Live numbers are on the dashboard. These are the §19 cadence reports: next business day, hypercare windows and campaign close."
      />
      <ul className="wdc-tabs" role="list">
        {REPORT_TABS.map((t) => (
          <li key={t.id}>
            <NavLink to={`/reports${t.id ? `/${t.id}` : ''}`} end>
              {t.label}
            </NavLink>
          </li>
        ))}
      </ul>
      <Outlet />
    </>
  );
}

function MetricsTable({ metrics, caption }: { metrics: MetricValueDto[]; caption: string }) {
  const intl = useIntl();
  return (
    <table className="wdc-table">
      <caption className="wd-sr-only">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Metric</th>
          <th scope="col">Value</th>
          <th scope="col">Definition</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map((m) => (
          <tr key={m.key}>
            <th scope="row">{m.label}</th>
            <td className="wd-tabular">{formatMetric(intl, m)}</td>
            <td className="wd-muted">{m.definition}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function NextBusinessDayTab() {
  const intl = useIntl();
  const [date, setDate] = useState(() => new Date(Date.now() - 86_400_000).toISOString().slice(0, 10));
  const query = useQuery({
    queryKey: ['reports', 'nbd', date],
    queryFn: () => analyticsApi.nextBusinessDay({ date }),
  });
  const r = query.data;
  return (
    <>
      <div className="wdc-form-grid" style={{ marginBottom: 'var(--wd-space-4)' }}>
        <label className="wd-field">
          <span className="wd-field__label">Report day</span>
          <input className="wd-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      {query.isError ? (
        <ErrorState
          title="Report unavailable"
          body="Retry in a moment."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : null}
      <div className="wdc-grid">
        <StatCard
          label="Reconciliation"
          loading={query.isPending}
          value={r ? intl.formatNumber(r.reconciliation.openExceptions) : null}
          hint={r ? `open exceptions · ${intl.formatNumber(r.reconciliation.runs)} runs` : undefined}
          signal={
            r
              ? r.reconciliation.blocksDraw
                ? { tone: 'error', text: 'A draw is blocked' }
                : { tone: 'success', text: 'No draw blocked' }
              : undefined
          }
        />
        <StatCard
          label="Claims SLA"
          loading={query.isPending}
          value={r ? intl.formatNumber(r.claimsSla.decided) : null}
          hint={
            r
              ? `decided · ${intl.formatNumber(r.claimsSla.withinSla)} within one business day · median ${r.claimsSla.medianHours === null ? '—' : `${intl.formatNumber(r.claimsSla.medianHours)} h`}`
              : undefined
          }
          signal={
            r
              ? r.claimsSla.breaches > 0
                ? { tone: 'error', text: `${r.claimsSla.breaches} breaches` }
                : { tone: 'success', text: 'No breaches' }
              : undefined
          }
        />
        <StatCard
          label="Fulfillment SLA"
          loading={query.isPending}
          value={r ? intl.formatNumber(r.fulfillmentSla.completed) : null}
          hint={
            r
              ? `completed · ${intl.formatNumber(r.fulfillmentSla.overdue)} overdue · ${intl.formatNumber(r.fulfillmentSla.exceptions)} exceptions`
              : undefined
          }
        />
        <StatCard
          label="Risk outcomes"
          loading={query.isPending}
          value={r ? intl.formatNumber(r.riskOutcomes.openReviews) : null}
          hint={
            r
              ? `open reviews · ${intl.formatNumber(r.riskOutcomes.blockedAttempts)} blocked attempts · ${intl.formatNumber(r.riskOutcomes.redraws)} redraws`
              : undefined
          }
        />
      </div>
      <div
        className="wdc-grid"
        style={{
          marginTop: 'var(--wd-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        }}
      >
        <BarsChart
          title="Channel quality"
          description="Entry starts and confirmed entries by source platform on the report day."
          data={(r?.channelQuality ?? []).map((c) => ({
            platform: humanizeStatus(c.platform),
            entryStarts: c.entryStarts,
            confirmedEntries: c.confirmedEntries,
          }))}
          xKey="platform"
          series={[
            { key: 'entryStarts', label: 'Entry starts', color: 'secondary' },
            { key: 'confirmedEntries', label: 'Confirmed entries' },
          ]}
          emptyHint="Appears with the first entry of the day."
        />
        <BarsChart
          title="Sponsor delivery"
          description="Viewable impressions and clicks per sponsor (identity suppression applied)."
          data={(r?.sponsorDelivery ?? []).map((s) => ({
            sponsor: s.sponsorName,
            impressions: s.viewableImpressions,
            clicks: s.clicks,
          }))}
          xKey="sponsor"
          series={[
            { key: 'impressions', label: 'Viewable impressions', color: 'secondary' },
            { key: 'clicks', label: 'Clicks' },
          ]}
          emptyHint="Appears once sponsors are placed."
        />
      </div>
    </>
  );
}

export function HypercareTab() {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['reports', 'hypercare'],
    queryFn: () => analyticsApi.hypercare(),
    refetchInterval: 60_000,
  });
  const r = query.data;
  if (query.isError)
    return (
      <ErrorState
        title="Hypercare report unavailable"
        body="Retry in a moment."
        action={<Button onClick={() => query.refetch()}>Retry</Button>}
      />
    );
  if (query.isPending || !r) return <Skeleton shape="block" height={200} label="Loading hypercare windows" />;
  return (
    <div style={{ display: 'grid', gap: 'var(--wd-space-4)' }}>
      <p className="wd-muted">
        {r.launchedAt
          ? `Launched ${intl.formatDateTime(r.launchedAt)} · as of ${intl.formatDateTime(r.asOf)}`
          : 'Hypercare starts with the first published lottery.'}
      </p>
      {r.windows.map((w) => (
        <section
          key={w.id}
          className="wd-surface"
          style={{ padding: 'var(--wd-space-4)' }}
          data-testid={`hypercare-${w.id}`}
        >
          <div className="wdc-page-header__row">
            <h2>{w.label}</h2>
            <ToneBadge tone={w.status === 'ACTIVE' ? 'info' : w.status === 'DONE' ? 'success' : 'neutral'}>
              {humanizeStatus(w.status)}
            </ToneBadge>
          </div>
          <p>
            <strong>Focus:</strong> {w.focus}
          </p>
          <p>
            <strong>Exit evidence:</strong> {w.exitEvidence}
          </p>
          {w.metrics.length ? (
            <MetricsTable metrics={w.metrics} caption={`${w.label} metrics`} />
          ) : (
            <p className="wd-muted">Metrics appear once the window opens.</p>
          )}
        </section>
      ))}
    </div>
  );
}

export function CampaignListTab() {
  const query = useQuery({ queryKey: ['lotteries', 'for-reports'], queryFn: () => lotteriesApi.list() });
  const items = query.data?.items ?? [];
  if (query.isPending) return <Skeleton shape="block" height={120} label="Loading lotteries" />;
  if (items.length === 0)
    return (
      <EmptyState
        title="No lotteries yet"
        body="Campaign close reports are generated per lottery."
        action={<Button href="/lotteries/new">Create lottery</Button>}
      />
    );
  return (
    <ul style={{ display: 'grid', gap: 'var(--wd-space-2)', listStyle: 'none', padding: 0 }}>
      {items.map((l) => (
        <li
          key={l.lotteryId}
          className="wd-surface"
          style={{
            padding: 'var(--wd-space-3)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <strong>{l.title}</strong> <span className="wd-muted">· {humanizeStatus(l.status)}</span>
          </span>
          <Link className="wd-btn" data-variant="secondary" to={`/reports/lotteries/${l.lotteryId}`}>
            Open campaign report
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function CampaignReportPage() {
  const { id = '' } = useParams();
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['reports', 'campaign', id],
    queryFn: () => analyticsApi.campaignReport(id),
    enabled: Boolean(id),
  });
  const r: CampaignReportDto | undefined = query.data;
  if (query.isPending) return <Skeleton shape="block" height={240} label="Loading campaign report" />;
  if (query.isError || !r)
    return (
      <ErrorState
        title="Report not found"
        body="The lottery may belong to another tenant."
        reference={isApiError(query.error) ? query.error.supportReference : undefined}
        action={<Button href="/reports/campaigns">Back to reports</Button>}
      />
    );
  const pct = (n: number, d: number) =>
    d > 0 ? `${intl.formatNumber(Math.round((n / d) * 1000) / 10)}%` : '—';
  const requestExport = (type: 'entries' | 'fairness' | 'ledger' | 'claims' | 'evidence-pack') =>
    exportsApi.lottery(r.lotteryId, type);
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Reports', to: '/reports/campaigns' }, { label: r.title }]}
        title={`${r.title} — campaign close`}
        meta={<ToneBadge tone="info">{humanizeStatus(r.status)}</ToneBadge>}
        description={`${r.slug} · generated ${intl.formatDateTime(r.generatedAt)}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => void requestExport('entries')}>
              Export entries (pseudonymous)
            </Button>
            <Button variant="secondary" onClick={() => void requestExport('fairness')}>
              Export fairness bundle
            </Button>
            <Button variant="secondary" onClick={() => void requestExport('evidence-pack')}>
              Export evidence pack
            </Button>
          </>
        }
      />
      <div className="wdc-grid">
        <StatCard
          label="Cohort funnel"
          value={intl.formatNumber(r.cohortFunnel.confirmedEntries)}
          hint={`confirmed entries · ${intl.formatNumber(r.cohortFunnel.views)} views → ${intl.formatNumber(r.cohortFunnel.entryStarts)} starts → ${intl.formatNumber(r.cohortFunnel.eligibleSubmissions)} submissions → ${intl.formatNumber(r.cohortFunnel.paymentInitialized)} payments · ${pct(r.cohortFunnel.confirmedEntries, r.cohortFunnel.entryStarts)} start→confirmed · ${intl.formatNumber(r.cohortFunnel.uniqueParticipants)} participants`}
        />
        <StatCard
          label="Incremental referrals"
          value={intl.formatNumber(r.incrementalReferrals.qualifiedReferrals)}
          hint={`qualified referrals · ${intl.formatNumber(r.incrementalReferrals.shares)} shares · ${intl.formatNumber(r.incrementalReferrals.convertedShares)} converted · ${intl.formatNumber(r.incrementalReferrals.bonusEntries)} bonus entries · k ${r.incrementalReferrals.kFactor === null ? '—' : intl.formatNumber(r.incrementalReferrals.kFactor)}`}
        />
        <StatCard
          label="Net financials (ledger)"
          value={
            r.netFinancials
              ? intl.formatMoney(r.netFinancials.netRevenue.amountMinor, r.netFinancials.currency)
              : null
          }
          hint={
            r.netFinancials
              ? `gross ${intl.formatMoney(r.netFinancials.grossEntries.amountMinor, r.netFinancials.currency)} · fees ${intl.formatMoney(r.netFinancials.fees.amountMinor, r.netFinancials.currency)} · refunds ${intl.formatMoney(r.netFinancials.refunds.amountMinor, r.netFinancials.currency)} · prize cost ${intl.formatMoney(r.netFinancials.prizeCost.amountMinor, r.netFinancials.currency)} · ROI ${r.netFinancials.roi === null ? '—' : intl.formatNumber(r.netFinancials.roi)}`
              : undefined
          }
          emptyHint="No ledger activity (free lottery)."
        />
        <StatCard
          label="Audience growth"
          value={intl.formatNumber(r.audienceGrowth.marketingOptIns)}
          hint={`marketing opt-ins · ${r.audienceGrowth.optInRate === null ? '—' : pct(r.audienceGrowth.optInRate * 100, 100)} of participants · ${intl.formatNumber(r.audienceGrowth.newParticipants)} new profiles · ${intl.formatNumber(r.audienceGrowth.withdrawals)} withdrawals`}
        />
        <StatCard
          label="Claims"
          value={intl.formatNumber(r.claims.paidOrDelivered)}
          hint={`paid or delivered of ${intl.formatNumber(r.claims.wins)} wins · ${intl.formatNumber(r.claims.claimsStarted)} started · ${intl.formatNumber(r.claims.claimsSubmitted)} submitted · ${intl.formatNumber(r.claims.approved)} approved · ${intl.formatNumber(r.claims.rejected)} rejected · median ${r.claims.medianHoursToClaim === null ? '—' : `${intl.formatNumber(r.claims.medianHoursToClaim)} h`} to claim`}
        />
      </div>
      <div
        className="wdc-grid"
        style={{
          marginTop: 'var(--wd-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        }}
      >
        <BarsChart
          title="Funnel by source platform"
          description="Views and confirmed entries per arrival platform."
          data={r.cohortFunnel.bySource.map((b) => ({
            platform: humanizeStatus(b.platform),
            views: b.views,
            confirmedEntries: b.confirmedEntries,
          }))}
          xKey="platform"
          series={[
            { key: 'views', label: 'Views', color: 'secondary' },
            { key: 'confirmedEntries', label: 'Confirmed entries' },
          ]}
          emptyHint="Appears with the first view."
        />
        <BarsChart
          title="Sponsor delivery"
          description="Viewable impressions, clicks and sample allocations per sponsor."
          data={r.sponsorDelivery.map((s) => ({
            sponsor: s.sponsorName,
            impressions: s.viewableImpressions,
            clicks: s.clicks,
            samples: s.samplesAllocated,
          }))}
          xKey="sponsor"
          series={[
            { key: 'impressions', label: 'Viewable impressions', color: 'secondary' },
            { key: 'clicks', label: 'Clicks' },
            { key: 'samples', label: 'Samples allocated', color: 'prize' },
          ]}
          emptyHint="Appears once sponsors are placed."
        />
      </div>
      <section
        className="wd-surface"
        style={{ padding: 'var(--wd-space-4)', marginTop: 'var(--wd-space-4)' }}
      >
        <h2>Lessons for the next template</h2>
        {r.lessons.length ? (
          <ul>
            {r.lessons.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        ) : (
          <p className="wd-muted">
            Participant research and the retrospective are human inputs (§23.2 days 15–30); record them in the
            evidence pack.
          </p>
        )}
      </section>
    </>
  );
}
